import { css } from "@emotion/react";
import styled from "@emotion/styled";
import {
  ChangeEvent,
  ChangeEventHandler,
  ComponentProps,
  FC,
  FocusEvent,
  ReactNode,
  RefObject,
  useCallback,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

import { EmailLetter } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  FormFieldProps,
  Heading,
  IPickerListItem,
  Icon,
  Input,
  Picker,
  PromoBannerV2,
  SpacingToken,
  Switch,
  Text,
} from "@livechat/design-system-react-components";

import TextAreaAutosizeField from "components/TextAreaAutosizeField";
import { Card, CardWrapper } from "components/cards";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { LivechatHelpCenter } from "constants/help-center";
import { SettingsRoutes } from "routes/settings/constants";
import { IContactForm } from "store/entities/contact-forms/interfaces";
import { selectLivechatGroups, selectLivechatGroupsDataStatus } from "store/entities/integrations";
import { DataStatus } from "store/entities/license";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { ValidationResult, validate } from "../validation";

const CustomizationContainer = styled.div`
  max-width: 800px;

  #code {
    font-family: monospace;
  }
`;

const ShowMoreContainer: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [contentShown, toggleContentShown] = useState(false);

  return (
    <Block marginV={withVar(SpacingToken.Spacing1)} style={{ position: "relative" }}>
      <Flex marginBottom={withVar(SpacingToken.Spacing6)} gap={withVar(SpacingToken.Spacing2)} alignItems="center">
        <Switch on={contentShown} onChange={() => toggleContentShown(!contentShown)} size="compact" />
        <Heading size="2xs" bold uppercase>
          More options
        </Heading>
      </Flex>
      {contentShown && children}
    </Block>
  );
};

type ContactFormProps = {
  data: Partial<IContactForm>;
  onChange(fieldName: keyof IContactForm): ChangeEventHandler;
  onConsentChange(item: "checkbox" | "info" | "disabled"): void;
  onLivechatGroupChange(groupIDs: string[]): void;
  onValidation(fieldName: keyof IContactForm, validationResult: ValidationResult): void;
  onDirty(fieldName: keyof IContactForm, pristine: boolean): void;
  onTeamChange(teamID: string): void;
  backButtonRef: RefObject<HTMLElement>;
  validations: { [Key in keyof IContactForm]?: [boolean, ValidationResult] };
  isDefault?: boolean;
};

interface IInputFormFieldProps extends FormFieldProps, Omit<ComponentProps<typeof Input>, "error"> {}

const InputFormField: FC<IInputFormFieldProps> = ({ labelText, error, id, value, onChange, onBlur, ...rest }) => {
  return (
    <FormField labelText={labelText} error={error} css={{ width: "100%" }}>
      <Input
        id={id}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={!!error}
        {...rest}
        data-1p-ignore
        css={{ width: "100%" }}
      />
    </FormField>
  );
};

export const ContactForm: FC<ContactFormProps> = ({
  data,
  onChange,
  onConsentChange,
  onTeamChange,
  onDirty,
  onValidation,
  onLivechatGroupChange,
  validations,
  isDefault = false,
}) => {
  const teams = useSelector(selectTeamsAlphabetically);
  const hasLivechatIntegration = useSelector(selectLivechatGroupsDataStatus) === DataStatus.Ready;
  const livechatGroups = useSelector(selectLivechatGroups);

  const teamRef = useRef(null);
  const groupsRef = useRef(null);
  const customizeRef = useRef(null);

  const handleChange = useCallback(
    (fieldName: keyof IContactForm) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        onDirty(fieldName, false);
        onChange(fieldName)(event);
        onValidation(fieldName, validate(fieldName, event.target.value));
      },
    [onChange, onDirty, onValidation]
  );

  const handleBlur = useCallback(
    (fieldName: keyof IContactForm) =>
      (_event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        onDirty(fieldName, true);
      },
    [onDirty]
  );

  const showError = useCallback(
    (fieldName: keyof IContactForm): string | undefined => {
      const [dirty, validation] = validations[fieldName] || [false, { type: "ok" }];

      if (dirty) {
        switch (validation.type) {
          case "length":
            return `Length exceeded (limit ${validation.limit})`;
          case "required":
            return fieldName === "livechatGroupIDs" ? "Group is required" : "Can't be blank";
          case "ok":
            return undefined;
          default:
        }
      }

      return undefined;
    },
    [validations]
  );

  const {
    name,
    header,
    caption,
    confirmation,
    nameLabel,
    emailLabel,
    subjectLabel,
    messageLabel,
    sendLabel,
    sendLoadingLabel,
    sendErrorLabel,
    sendSuccessLabel,
    sendErrorMessage,
    consentMode,
    consentMessage,
    nameRequiredError,
    emailRequiredError,
    emailInvalidError,
    subjectRequiredError,
    messageRequiredError,
    consentRequiredError,
    teamID,
    livechatGroupIDs,
  } = data;

  const consent = {
    mode: consentMode || "disabled",
    ...(!consentMode || consentMode !== "disabled" ? { consentMessage } : {}),
  };

  const consentOptions: IPickerListItem[] = [
    { key: "checkbox", name: "Consent required to send the message" },
    { key: "info", name: "Only information" },
    { key: "disabled", name: "Disabled" },
  ];

  const selectedConsentOptions = consentOptions.filter(({ key }) => key === consent.mode);

  const handleConsentSelect = (selectedOptions: IPickerListItem[] | null): void => {
    if (selectedOptions && selectedOptions.length) {
      onConsentChange(selectedOptions[0].key as "checkbox" | "info" | "disabled");
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const teamOptions: IPickerListItem[] = teams.map((team: any) => ({
    key: team.id,
    name: team.name,

    customElement: {
      selectedItemBody: <TeamPickerSelectedListItem name={team.name} isDefault={team.isDefault} pickerType="single" />,
      listItemBody: <TeamPickerListItem name={team.name} isDefault={team.isDefault} pickerType="single" />,
    },
  }));

  const selectedTeamOptions = teamOptions.filter(({ key }): boolean => key === teamID);

  const handleTeamSelect = (selectedOptions: IPickerListItem[] | null): void => {
    if (selectedOptions && selectedOptions.length) {
      onTeamChange(selectedOptions[0].key);
    }
  };

  const livechatGroupsOptions: IPickerListItem[] = isDefault
    ? [{ key: "default", name: "All unassigned groups" }]
    : // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      livechatGroups.map((group: any) => ({
        key: group.id,
        name: group.name,
      }));

  const livechatGroupsSelectedOptions: IPickerListItem[] = isDefault
    ? [
        {
          key: "default",
          name: "All unassigned groups",
        },
      ]
    : livechatGroupsOptions.filter(({ key }) => livechatGroupIDs?.includes(key));

  const handleLivechatGroupSelect = (selectedOptions: IPickerListItem[] | null): void => {
    if (selectedOptions?.length) {
      onLivechatGroupChange(selectedOptions.map(option => option.key));
    } else {
      onLivechatGroupChange([]);
    }
  };

  const [activeCard, setActiveCard] = useState<"customize-form" | "embed-code" | null>(null);
  const isNewForm = data.id === undefined;

  const handleCardOpen = useCallback(
    (cardId: "customize-form" | "embed-code") => {
      if (activeCard === cardId) {
        setActiveCard(null);
        return;
      }

      if (isNewForm && cardId === "embed-code") {
        return;
      }

      setActiveCard(cardId);
    },
    [activeCard, isNewForm]
  );

  return (
    <CustomizationContainer>
      <Block maxWidth="400px" marginBottom={withVar(SpacingToken.Spacing4)}>
        <InputFormField
          type="text"
          id="name"
          data-testid="contact-form-name-input"
          value={name}
          onChange={handleChange("name")}
          onBlur={handleBlur("name")}
          error={showError("name")}
          labelText="Contact form name"
          disabled={isDefault}
          placeholder="Enter contact form name..."
        />
        <div ref={teamRef}>
          <FormField labelText="Assign tickets from this form to team" css={{ width: "100%" }}>
            <Picker
              disabled={isDefault}
              options={teamOptions}
              selected={selectedTeamOptions}
              onSelect={handleTeamSelect}
              maxListHeight={200}
              hideClearButton
            />
          </FormField>
        </div>
      </Block>
      {hasLivechatIntegration && livechatGroups && (
        <Block ref={groupsRef} marginBottom={withVar(SpacingToken.Spacing4)}>
          <Heading as="h2" size="xs">
            Publish on LiveChat widget
          </Heading>
          <PromoBannerV2
            css={{
              margin: `${withVar(SpacingToken.Spacing4)} 0`,
              background: withVar(DesignToken.SurfaceGradient07),
            }}
          >
            <Text>
              Display the HelpDesk contact form on the LiveChat widget&apos;s welcome screen to let customers leave
              messages when agents are offline.{" "}
              <Button target="_blank" kind="link-light" href={LivechatHelpCenter.ADD_HELPDESK_TO_THE_CHAT_WIDGET}>
                Learn more
              </Button>
            </Text>
          </PromoBannerV2>
          <Block maxWidth="400px">
            <FormField
              labelText="Available in the widget for the following groups"
              error={showError("livechatGroupIDs")}
              description={
                <Text
                  size="sm"
                  customColor={withVar(DesignToken.ContentBasicSecondary)}
                  css={css({ textWrap: "nowrap" })}
                >
                  {isDefault ? (
                    <>
                      If you want a different form for a specific group,{" "}
                      <Button kind="link-light" href={`${SettingsRoutes.WidgetForms}/add`}>
                        create a new form
                      </Button>
                    </>
                  ) : (
                    <>LiveChat group can have only one contact form assigned to it.</>
                  )}
                </Text>
              }
              css={{ width: "100%" }}
            >
              <Picker
                error={Boolean(showError("livechatGroupIDs"))}
                id="livechat-groups-select"
                type="multi"
                disabled={isDefault}
                options={livechatGroupsOptions}
                selected={livechatGroupsSelectedOptions}
                onSelect={handleLivechatGroupSelect}
              />
            </FormField>
          </Block>
        </Block>
      )}
      <Block paddingBottom="100px">
        <Heading as="h2" size="xs">
          Customize
        </Heading>
        <CardWrapper activeCard={activeCard as string} onCardOpen={handleCardOpen}>
          <Card
            headerRef={customizeRef}
            title={
              <Flex alignItems="center">
                <Block paddingRight="20px">
                  <Icon source={EmailLetter} size="large" />
                </Block>
                <Heading size="xs">Customize form fields</Heading>
              </Flex>
            }
            id="customize-form"
          >
            <InputFormField
              labelText="Header"
              onChange={handleChange("header")}
              onBlur={handleBlur("header")}
              error={showError("header")}
              id="header"
              value={header}
              placeholder="Header of the form e.g., Contact Our Company"
            />
            <TextAreaAutosizeField
              labelText="Caption"
              onChange={handleChange("caption")}
              onBlur={handleBlur("caption")}
              error={showError("caption")}
              value={caption}
              id="caption"
              placeholder="Caption below the header - use it to provide context why this form should be used"
              css={{ marginBottom: withVar(SpacingToken.Spacing5) }}
            />
            <InputFormField
              labelText="Confirmation after sending the form"
              onChange={handleChange("confirmation")}
              onBlur={handleBlur("confirmation")}
              error={showError("confirmation")}
              id="confirmation"
              value={confirmation}
              placeholder="Confirmation message your client is going to see after sending message"
            />
            <Block marginTop={withVar(SpacingToken.Spacing2)}>
              <Heading size="xs" css={{ marginBottom: withVar(SpacingToken.Spacing4) }}>
                Form fields
              </Heading>

              <InputFormField
                id="name-label"
                value={nameLabel}
                labelText="Name"
                onChange={handleChange("nameLabel")}
                onBlur={handleBlur("nameLabel")}
                error={showError("nameLabel")}
              />
              <InputFormField
                id="email-label"
                value={emailLabel}
                labelText="Email"
                onChange={handleChange("emailLabel")}
                onBlur={handleBlur("emailLabel")}
                error={showError("emailLabel")}
              />
              <InputFormField
                id="subject-label"
                value={subjectLabel}
                labelText="Subject"
                onChange={handleChange("subjectLabel")}
                onBlur={handleBlur("subjectLabel")}
                error={showError("subjectLabel")}
              />
              <InputFormField
                id="message-label"
                value={messageLabel}
                labelText="Message"
                onChange={handleChange("messageLabel")}
                onBlur={handleBlur("messageLabel")}
                error={showError("messageLabel")}
              />
              <FormField labelText="Data processing consent" css={{ width: "100%" }}>
                <Picker
                  id="consent"
                  options={consentOptions}
                  onSelect={handleConsentSelect}
                  selected={selectedConsentOptions}
                  searchDisabled
                  hideClearButton
                />
              </FormField>
              {["info", "checkbox"].includes(consent.mode) && (
                <TextAreaAutosizeField
                  id="consent-message"
                  value={consent.consentMessage || ""}
                  onChange={handleChange("consentMessage")}
                  onBlur={handleBlur("consentMessage")}
                  error={showError("consentMessage")}
                  placeholder="Consent message. You can consult https://www.livechat.com/help/prepare-chat-gdpr/ for details."
                  description="You can use [link label](link address) syntax to include links in your consent message."
                  css={{ marginBottom: withVar(SpacingToken.Spacing5) }}
                />
              )}
              <InputFormField
                id="action"
                value={sendLabel}
                labelText="Button text"
                onChange={handleChange("sendLabel")}
                onBlur={handleBlur("sendLabel")}
                error={showError("sendLabel")}
              />
              <ShowMoreContainer>
                <InputFormField
                  id="action-loading"
                  value={sendLoadingLabel}
                  labelText="Button text when sending"
                  onChange={handleChange("sendLoadingLabel")}
                  onBlur={handleBlur("sendLoadingLabel")}
                  error={showError("sendLoadingLabel")}
                />
                <InputFormField
                  id="action-success"
                  value={sendSuccessLabel}
                  labelText="Button text on success"
                  onChange={handleChange("sendSuccessLabel")}
                  onBlur={handleBlur("sendSuccessLabel")}
                  error={showError("sendSuccessLabel")}
                />
                <Block marginTop={withVar(SpacingToken.Spacing2)}>
                  <Heading size="xs" css={{ marginBottom: withVar(SpacingToken.Spacing4) }}>
                    Error states
                  </Heading>
                  <InputFormField
                    id="action-error"
                    value={sendErrorLabel}
                    labelText="Button text on error"
                    onChange={handleChange("sendErrorLabel")}
                    onBlur={handleBlur("sendErrorLabel")}
                    error={showError("sendErrorLabel")}
                  />
                  <TextAreaAutosizeField
                    id="error-message"
                    value={sendErrorMessage}
                    labelText="Error message if send failed"
                    onChange={handleChange("sendErrorMessage")}
                    onBlur={handleBlur("sendErrorMessage")}
                    error={showError("sendErrorMessage")}
                    css={{ marginBottom: withVar(SpacingToken.Spacing5) }}
                  />
                  <Block marginTop={withVar(SpacingToken.Spacing2)}>
                    <Heading size="xs" css={{ marginBottom: withVar(SpacingToken.Spacing4) }}>
                      Validation messages
                    </Heading>
                    <InputFormField
                      id="name-blank"
                      value={nameRequiredError}
                      labelText="Name is blank"
                      onChange={handleChange("nameRequiredError")}
                      onBlur={handleBlur("nameRequiredError")}
                      error={showError("nameRequiredError")}
                    />
                    <InputFormField
                      id="email-blank"
                      value={emailRequiredError}
                      labelText="Email is blank"
                      onChange={handleChange("emailRequiredError")}
                      onBlur={handleBlur("emailRequiredError")}
                      error={showError("emailRequiredError")}
                    />
                    <InputFormField
                      id="email-invalid"
                      value={emailInvalidError}
                      labelText="Invalid email"
                      onChange={handleChange("emailInvalidError")}
                      onBlur={handleBlur("emailInvalidError")}
                      error={showError("emailInvalidError")}
                    />
                    <InputFormField
                      id="subject-blank"
                      value={subjectRequiredError}
                      labelText="Subject is blank"
                      onChange={handleChange("subjectRequiredError")}
                      onBlur={handleBlur("subjectRequiredError")}
                      error={showError("subjectRequiredError")}
                    />
                    <InputFormField
                      id="message-blank"
                      value={messageRequiredError}
                      labelText="Message is blank"
                      onChange={handleChange("messageRequiredError")}
                      onBlur={handleBlur("messageRequiredError")}
                      error={showError("messageRequiredError")}
                    />
                    <InputFormField
                      id="consent-blank"
                      value={consentRequiredError}
                      labelText="No consent to the processing of personal data"
                      onChange={handleChange("consentRequiredError")}
                      onBlur={handleBlur("consentRequiredError")}
                      error={showError("consentRequiredError")}
                    />
                  </Block>
                </Block>
              </ShowMoreContainer>
            </Block>
          </Card>
        </CardWrapper>
      </Block>
    </CustomizationContainer>
  );
};
